import React, { useEffect, useState, memo } from 'react';
import { setActivityStatus } from '../../services/pkiApi';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://v2.schmalgsicht.de';

export const TicTacToe3D = memo(function TicTacToe3D({ profileId, onClose, onStatusChange }) {
  const [board, setBoard] = useState(Array(9).fill(null));
  const [gameOver, setGameOver] = useState(false);
  const [isChrisThinking, setIsChrisThinking] = useState(false);

  // Post game result to backend
  const postGameResult = async (result, finalBoard) => {
    if (!profileId) {
      return;
    }

    try {
      const response = await fetch(`${API_BASE_URL}/api/profile/${profileId}/tictactoe`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          result,
          board: finalBoard,
          timestamp: new Date().toISOString()
        })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || `HTTP error! status: ${response.status}`);
      }

      await response.json();

      // Set activity status for external API
      await setActivityStatus(profileId, 'tictactoe');
    } catch (error) {
      // Silent error handling
    }
  };

  // Set initial status
  useEffect(() => {
    onStatusChange('Spiele Tic Tac Toe gegen Chris');
  }, [onStatusChange]);

  const calculateWinner = (squares) => {
    const lines = [
      [0, 1, 2], [3, 4, 5], [6, 7, 8],
      [0, 3, 6], [1, 4, 7], [2, 5, 8],
      [0, 4, 8], [2, 4, 6]
    ];
    for (let [a, b, c] of lines) {
      if (squares[a] && squares[a] === squares[b] && squares[a] === squares[c]) {
        return squares[a];
      }
    }
    return null;
  };

  const getEmptySquares = (squares) => {
    return squares.map((val, idx) => val === null ? idx : null).filter(val => val !== null);
  };

  const makeModeratorMove = (currentBoard) => {
    const emptySquares = getEmptySquares(currentBoard);
    if (emptySquares.length === 0) return;

    const lines = [
      [0, 1, 2], [3, 4, 5], [6, 7, 8],
      [0, 3, 6], [1, 4, 7], [2, 5, 8],
      [0, 4, 8], [2, 4, 6]
    ];

    // Try to win
    for (let [a, b, c] of lines) {
      if (currentBoard[a] === 'O' && currentBoard[b] === 'O' && currentBoard[c] === null) return c;
      if (currentBoard[a] === 'O' && currentBoard[c] === 'O' && currentBoard[b] === null) return b;
      if (currentBoard[b] === 'O' && currentBoard[c] === 'O' && currentBoard[a] === null) return a;
    }

    // Try to block
    for (let [a, b, c] of lines) {
      if (currentBoard[a] === 'X' && currentBoard[b] === 'X' && currentBoard[c] === null) return c;
      if (currentBoard[a] === 'X' && currentBoard[c] === 'X' && currentBoard[b] === null) return b;
      if (currentBoard[b] === 'X' && currentBoard[c] === 'X' && currentBoard[a] === null) return a;
    }

    if (currentBoard[4] === null) return 4;
    return emptySquares[Math.floor(Math.random() * emptySquares.length)];
  };

  const handleClick = (index) => {
    // Block clicks while Chris is thinking, if cell is taken, or game is over
    if (isChrisThinking || board[index] || gameOver) return;

    const newBoard = [...board];
    newBoard[index] = 'X';
    setBoard(newBoard);

    const winner = calculateWinner(newBoard);
    if (winner) {
      setGameOver(true);
      onStatusChange('Du gewinnst!');
      postGameResult('player_win', newBoard);
      // Auto-close after 2 seconds
      setTimeout(() => onClose(), 2000);
      return;
    }

    if (!newBoard.includes(null)) {
      setGameOver(true);
      onStatusChange('Unentschieden!');
      postGameResult('draw', newBoard);
      setTimeout(() => onClose(), 2000);
      return;
    }

    // Show "Chris ist dran" and block input
    setIsChrisThinking(true);
    onStatusChange('Chris denkt nach...');

    // Random delay between 1.5 and 3 seconds to feel more human
    const thinkingTime = 1500 + Math.random() * 1500;

    setTimeout(() => {
      const moderatorMove = makeModeratorMove(newBoard);
      if (moderatorMove !== undefined) {
        newBoard[moderatorMove] = 'O';
        setBoard([...newBoard]);

        const moderatorWinner = calculateWinner(newBoard);
        if (moderatorWinner) {
          setGameOver(true);
          onStatusChange('Chris gewinnt!');
          postGameResult('chris_win', newBoard);
          setTimeout(() => onClose(), 2000);
        } else if (!newBoard.includes(null)) {
          setGameOver(true);
          onStatusChange('Unentschieden!');
          postGameResult('draw', newBoard);
          setTimeout(() => onClose(), 2000);
        } else {
          onStatusChange('Du bist dran');
        }
      }
      // Allow input again
      setIsChrisThinking(false);
    }, thinkingTime);
  };

  return (
    <div className="flex flex-col items-center gap-6 mt-8">
      {/* Game Board */}
      <div className="grid grid-cols-3 gap-3">
        {board.map((cell, index) => (
          <button
            key={index}
            onClick={() => handleClick(index)}
            className={`w-20 h-20 sm:w-24 sm:h-24 lg:w-28 lg:h-28 rounded-2xl text-4xl sm:text-5xl font-bold flex items-center justify-center transition-all duration-200 border-2 ${
              cell === 'X'
                ? 'bg-gradient-to-br from-blue-400 to-blue-600 text-white shadow-lg border-blue-300'
                : cell === 'O'
                ? 'bg-gradient-to-br from-pink-400 to-pink-600 text-white shadow-lg border-pink-300'
                : 'bg-white shadow-md border-gray-300 hover:bg-gray-50 hover:shadow-lg hover:border-gray-400'
            }`}
            disabled={gameOver || cell !== null}
          >
            {cell}
          </button>
        ))}
      </div>

      {/* Abbrechen Button */}
      <button
        onClick={onClose}
        className="px-6 py-2 rounded-full bg-gray-200 hover:bg-gray-300 text-gray-700 font-medium transition-all duration-300"
      >
        Abbrechen
      </button>
    </div>
  );
});
